<template>
  <div
    v-if="
      settings.appointment_overview_request &&
      !widgetSettings.bookingId &&
      !hasServices &&
      widgetSettings.type === 'booking'
    "
    :class="$style.base"
    v-test="'appointment-overview-banner'"
  >
    <div :class="$style.content">
      <transition mode="out-in">
        <div
          v-if="currentStep === 1"
          :class="[$style.step, $style.step1]"
          v-test="'appointment-overview-step-1'"
        >
          <div :class="$style.inner">
            <span :class="$style.text">
              {{ $t('appointment_overview_request.banner.heading') }}
            </span>
            <span
              :class="[$style.text, $style.link]"
              :style="style"
              @click="onClick"
              v-test="'appointment-overview-action'"
            >
              {{ $t('appointment_overview_request.banner.action') }}
            </span>
          </div>
          <div :class="$style.background" :style="backgroundStyle" />
        </div>
        <div v-else-if="currentStep === 2" :class="[$style.step, $style.step2]">
          <div
            :class="$style.btnClose"
            @click="onCloseClick"
            v-test="'step-2-close'"
          >
            <IconClose />
          </div>
          <div :class="$style.heading">
            {{ $t('appointment_overview_request.modal.heading') }}
          </div>
          <div :class="$style.description">
            {{ $t('appointment_overview_request.modal.description') }}
          </div>
          <form
            :class="$style.form"
            @submit.prevent="onSubmit"
            v-test="'request-appointment-overview-form'"
          >
            <BaseFormInput
              v-model="formData.email"
              :label="$t('appointment_overview_request.modal.placeholder')"
              :type="'email'"
              required
              name="email"
              :class="$style.input"
              v-test="'form-input-email'"
            />
            <p
              v-if="showAlreadyRequestedError"
              :class="$style.error"
              v-test="'already-requested-error'"
            >
              {{ $t('appointment_overview_request.modal.error') }}
            </p>
            <BaseButton
              :class="$style.button"
              v-test="'btn-submit-appointment-overview-request'"
            >
              <div :class="$style.btnInner">
                <IconMessage :class="$style.btnIcon" inverted />
                {{ $t('appointment_overview_request.modal.button') }}
              </div>
            </BaseButton>
          </form>
        </div>
        <div
          v-else-if="currentStep === 3"
          :class="[$style.step, $style.step3]"
          v-test="'appointment-overview-completed'"
        >
          <div
            :class="$style.btnClose"
            @click="onCloseClick"
            v-test="'appointment-overview-completed-close'"
          >
            <IconClose />
          </div>
          <div
            :class="$style.sentLabel"
            :style="{
              color: widgetSettings.color
            }"
          >
            <div :class="$style.sentLabelInner">
              <IconMessage :class="$style.btnIcon" />
              {{ $t('appointment_overview_request.completed_form.sent_label') }}
            </div>
            <div
              :class="$style.sentLabelBg"
              :style="{
                'background-color': widgetSettings.color
              }"
            />
          </div>
          <div
            :class="$style.heading"
            v-test="'appointment-overview-request-completed-header'"
          >
            {{
              $t('appointment_overview_request.completed_form.heading', {
                email: formData.email
              })
            }}
          </div>
          <div :class="$style.description">
            {{ $t('appointment_overview_request.completed_form.description') }}
          </div>
        </div>
      </transition>
    </div>
    <transition>
      <div
        v-show="currentStep > 1"
        :class="$style.overlay"
        @click="onCloseClick"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import axios from 'axios';
import { storeToRefs } from 'pinia';
import { inject } from 'vue';

import config from '@/config';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import widgetSettings from '@/widget-settings';

import BaseButton from '@/components/BaseButton.vue';
import BaseFormInput from '@/components/base-form-input/index.vue';
import IconClose from '@/components/IconClose.vue';
import IconMessage from '@/components/IconMessage.vue';

export default {
  components: {
    BaseButton,
    BaseFormInput,
    IconMessage,
    IconClose
  },
  setup() {
    const { company, settings } = useCompanyStore();
    const { serviceIds } = storeToRefs(useBookingStore());
    const mixpanel = inject<any>('mixpanel');

    return {
      company,
      settings,
      serviceIds,
      widgetSettings,
      mixpanel
    };
  },
  data() {
    return {
      currentStep: 1,
      showAlreadyRequestedError: false,
      formData: { email: '' },
      hasServices: false
    };
  },
  watch: {
    'serviceIds.length': {
      handler(value) {
        // Settimeout is needed to fix a transition bug where the service summary jumps to the top for one frame when this animates
        setTimeout(
          () => {
            this.hasServices = !!value;
          },
          value ? 0 : 100
        );
      },
      immediate: true
    }
  },
  computed: {
    backgroundStyle() {
      return {
        'background-color': this.widgetSettings.color
      };
    },
    style() {
      return {
        color: this.widgetSettings.color
      };
    },
    useMixpanel() {
      const { settings } = useCompanyStore();
      return settings.appointment_overview_tracking;
    }
  },
  methods: {
    onClick() {
      this.currentStep++;
      if (this.useMixpanel) {
        this.mixpanel.track('Appointment Request - Open Modal');
      }
    },
    onSubmit() {
      this.showAlreadyRequestedError = false;

      if (this.widgetSettings.mock) {
        this.onFormSubmitted();
        return;
      }

      axios
        .post(
          `${config.API_URL}/companies/${this.company.id}/appointments_overview/request`,
          this.formData
        )
        .then((response) => {
          this.onFormSubmitted();
        })
        .catch(({ response: { data } }) => {
          if (!data) {
            return;
          }

          if (data.email_status === 'already_requested') {
            this.showAlreadyRequestedError = true;
            if (this.useMixpanel) {
              this.mixpanel.track(
                'Appointment Request - email already requested'
              );
            }
          } else if (data.email_status === 'not_sent') {
            this.onFormSubmitted();
            if (this.useMixpanel) {
              this.mixpanel.track('Appointment Request - email not sent');
            }
          }
        });
    },
    onFormSubmitted() {
      this.currentStep++;
    },
    onCloseClick() {
      this.currentStep = 1;
      this.showAlreadyRequestedError = false;
      this.formData.email = '';
      if (this.useMixpanel) {
        this.mixpanel.track('Appointment Request - Close Modal');
      }
    }
  }
};
</script>

<style lang="scss" module>
.base {
  position: absolute;
  bottom: 0;
  z-index: 4;
  width: 100%;
}

.overlay {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 100vh;
  width: 100%;
  background: rgba(0, 0, 0, 0.7);
  transition: opacity 0.1s;

  &:global(.v-enter-from),
  &:global(.v-leave-to) {
    opacity: 0;
  }
}

.step {
  background-color: white;
  max-height: 400px;
  transition: transform 0.15s ease-out;

  &:global(.v-enter-from),
  &:global(.v-leave-to) {
    transform: translateY(100%);
  }
}

.step1 {
  padding: 16px 14px 18px;
}

.step2,
.step3 {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  padding: 24px;
}

.content {
  position: relative;
  z-index: 1;
}

.background {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0.1;
  z-index: 1;
}

.inner {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.text {
  font-weight: bold;
}

.link {
  cursor: pointer;
  flex-shrink: 0;
  margin-left: $spacing * 0.5;
}

.error {
  color: $color-error;
  font-size: 13px;
  margin-top: $spacing * -1;
  margin-bottom: $spacing * 2;
}

.heading {
  font-weight: bold;
  font-size: 18px;
  padding-right: 55px;
}

.heading,
.description,
.input {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 12px;
  }
}

.button {
  width: 100%;
}

.sentLabel {
  display: inline-block;
  padding: 10px 14px 11px 10px;
  position: relative;
  font-weight: bold;
  margin-bottom: 24px;
}

.sentLabelInner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;

  & > *:first-child {
    margin-right: 4px;
  }
}

.sentLabelBg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0.1;
  border-radius: 8px;
}

.btnInner {
  display: flex;
  align-items: center;

  & > *:first-child {
    margin-right: 8px;
  }
}

.btnClose {
  position: absolute;
  right: 0;
  top: 0;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
</style>
